'use strict'

// Field descriptors the live table, cards, maps, and calendar views read.
// A cards or card view may nest those fields in cardRow and cardColumn nodes.
// The descriptors stay in one list so record values keep a stable key, and
// the layout repeats those same descriptors in row and column order.

const { isColumnUserSortable } = require('./view-sort')
const { flattenCardFields, cardLayoutRows, cardColumnWidth, isCardView } = require('./card-layout')
const { columnDisplay } = require('./table-layout')
const { normalizeMultiselectDisplay } = require('./multiselect-display')

function linkKeyFor (field) {

  if (field.type === 'link' && field.linkMode === 'code') return null

  if (field.type === 'link' && field.linkMode === 'relatedRecord' && field.relationshipColumn) {

    return `${field.childPageUniqueTitle}__rel__${field.relationshipColumn}`
  }

  return field.childPageUniqueTitle || null
}

function viewLinkRunsCode (field) {

  return !!(field && field.type === 'link' && field.linkMode === 'code')
}

function viewLinkUsesButton (field) {

  if (!field || !field.displayAsButton) return false
  if (field.type && field.type !== 'link') return false

  return true
}

function viewRecordLink (item, field) {

  if (!item || !field) return ''

  const linkKey = field.linkKey || field.childPageUniqueTitle

  if (!linkKey) return ''

  const primary = item._links && item._links[linkKey]

  if (primary != null && primary !== '') return String(primary)

  const secondary = item.links && item.links[linkKey]

  if (secondary == null || secondary === '') return ''

  return String(secondary)
}

function viewLinkHref (rawLink, route) {

  if (!rawLink) return ''

  const value = String(rawLink)
  const routePath = route && route.routePath
  const application = route && route.application

  if (value.includes('/')) {

    const base = String(routePath || '').replace(/\/+$/, '')

    return base ? `${base}/${value}` : `/${value.replace(/^\/+/, '')}`
  }

  if (!application) return ''

  return `/${application}/${value}`
}

function viewLinkResolvesOnServer (field) {

  return !!(field && field.type === 'link' && field.linkMode !== 'code')
}

function assignViewLink (component, config) {

  if (!component || !config) return component

  const mode = config.linkMode === 'relatedRecord' || config.linkMode === 'static' || config.linkMode === 'code'
    ? config.linkMode
    : 'record'

  component.linkMode = mode
  component.relationshipColumn = mode === 'relatedRecord' ? (config.relationshipColumn || null) : null

  if (mode === 'code') {

    component.code = typeof config.code === 'string' ? config.code : ''
    component.childPageUniqueTitle = ''
  } else if (Object.prototype.hasOwnProperty.call(component, 'code')) {

    delete component.code
  }

  if (config.displayAsButton) component.displayAsButton = true
  else delete component.displayAsButton

  return component
}

// A stored label is shown as written. An empty string is a choice to show
// no label. A field that never stored one still uses its column id.
function viewFieldSelector (uniqueTitle) {

  return `[data-field="${String(uniqueTitle || '')}"]`
}

function viewFieldRecordAccess (uniqueTitle) {

  const name = String(uniqueTitle || '')

  if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(name)) return `turbohammerRow.${name}`

  return `turbohammerRow[${JSON.stringify(name)}]`
}

// The fields a custom-code link can reach on the clicked table row or card.
function viewCodeFieldGuide (components) {

  const seen = new Set()
  const fields = []

  for (const field of flattenCardFields(components)) {

    if (!field || !field.uniqueTitle || seen.has(field.uniqueTitle)) continue
    if (columnDisplay(field).hidden) continue

    seen.add(field.uniqueTitle)

    const label = viewFieldLabel(field) || field.uniqueTitle

    const selector = viewFieldSelector(field.uniqueTitle)

    fields.push({
      uniqueTitle: field.uniqueTitle,
      label,
      selector,
      query: `turbohammerElement.querySelector('${selector}')`,
      record: viewFieldRecordAccess(field.uniqueTitle)
    })
  }

  return fields
}

function viewFieldLabel (field) {

  if (typeof field.label === 'string') return field.label.trim()

  return field.uniqueTitle || ''
}

function describeViewField (view, field, index) {

  const label = viewFieldLabel(field)
  const display = columnDisplay(field)

  return {
    type: field.type,
    key: String(index),
    label,
    customText: field.customText,
    sortable: field.type === 'action' ? false : isColumnUserSortable(view, field),
    fieldType: field.fieldType,
    childPageUniqueTitle: field.childPageUniqueTitle,
    linkMode: field.linkMode || null,
    relationshipColumn: field.relationshipColumn || null,
    linkKey: linkKeyFor(field),
    displayAsButton: viewLinkUsesButton(field),
    code: viewLinkRunsCode(field) ? (field.code || '') : '',
    editable: !!field.editable,
    placeholder: field.placeholder || '',
    description: field.description || '',
    required: !!field.required,
    columnType: field.columnType || '',
    validationRules: field.validationRules || { rules: [] },
    columnUniqueTitle: field.uniqueTitle,
    width: display.width,
    emptyText: display.emptyText,
    summary: display.summary,
    multiselectDisplay: normalizeMultiselectDisplay(field.multiselectDisplay),
    valueFormatting: field.valueFormatting && field.valueFormatting.type ? field.valueFormatting : null,
    actionKind: field.actionKind || '',
    submissionRules: field.submissionRules || { rules: [] }
  }
}

function buildViewFieldModel (view) {

  const card = isCardView(view)
  const leaves = card ? flattenCardFields(view && view.components) : ((view && view.components) || [])
  const fieldCounterMap = {}
  const fields = []
  const byField = new Map()
  let index = 0

  for (const field of leaves) {

    if (!field || columnDisplay(field).hidden) continue

    fieldCounterMap[field.uniqueTitle] = index
    const described = describeViewField(view, field, index)
    fields.push(described)
    byField.set(field, described)
    index += 1
  }

  const layout = card
    ? cardLayoutRows(view && view.components).map(row => ({
      columns: (row.components || []).map(column => {
        const laid = {
          fields: (column.components || []).map(field => byField.get(field)).filter(Boolean)
        }
        const width = cardColumnWidth(column)

        if (width) laid.columnFormat = width

        return laid
      })
    }))
    : null

  return { fields, fieldCounterMap, layout }
}

module.exports = {
  assignViewLink,
  buildViewFieldModel,
  viewCodeFieldGuide,
  viewLinkHref,
  viewLinkResolvesOnServer,
  viewLinkRunsCode,
  viewLinkUsesButton,
  viewRecordLink
}
